import { useEffect, useRef, useState, type ReactNode } from 'react';
import IframePreview from './IframePreview';

// Desktop-width preview: the frame is laid out at a real 1100px viewport (so
// desktop breakpoints fire) and visually scaled down to fit the column. The
// frame is an IframePreview: the page gets the published pages' stylesheet,
// never the staff app's.
// `className` overrides the frame height (the template preview passes its own).
export default function DesktopPreview({ children, className, title = 'Desktop preview' }: {
  children: ReactNode;
  className?: string;
  /** The frame's accessible name. */
  title?: string;
}) {
  const FRAME_W = 1100;
  const ref = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setBox({ w: el.clientWidth, h: el.clientHeight });
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const scale = box.w ? Math.min(1, box.w / FRAME_W) : 1;
  return (
    <div ref={ref} className={`${className ?? 'h-[calc(100vh-8rem)] max-h-[820px]'} w-full overflow-hidden rounded-2xl border border-border bg-bg`}>
      {/* `margin: 0 auto` on the frame below centers it once the panel is WIDER
          than FRAME_W and `scale` has clamped to 1. Without it the frame stays
          glued to the left and the surplus reads as dead space on wide
          displays. Below FRAME_W the element is wider than its container, so
          auto margins resolve to 0 and the top-left origin keeps the scaled
          result filling the panel exactly. One property, both cases. */}
      {box.w > 0 && (
        <div style={{ width: FRAME_W, height: box.h / scale, transform: `scale(${scale})`, transformOrigin: 'top left', margin: '0 auto' }}>
          <IframePreview className="h-full w-full" title={title}>{children}</IframePreview>
        </div>
      )}
    </div>
  );
}
